import type {Components} from 'react-markdown';

/**
 * Shared markdown renderer overrides for prose-style content (org knowledge,
 * MDM long-text fields) — larger/roomier than the chat-bubble variant in
 * ``OttoChatTranscript``'s ``MarkdownMessage``, which is tuned for a narrower
 * column. Block vs inline code is distinguished by the ``language-`` class
 * react-markdown puts on fenced blocks.
 */
export const markdownComponents: Components = {
  h1: ({children}) => (
    <h1 className="mb-3 mt-6 text-lg font-bold first:mt-0">{children}</h1>
  ),
  h2: ({children}) => (
    <h2 className="mb-2 mt-6 text-base font-bold first:mt-0">{children}</h2>
  ),
  h3: ({children}) => (
    <h3 className="mb-2 mt-4 text-sm font-bold first:mt-0">{children}</h3>
  ),
  h4: ({children}) => (
    <h4 className="mb-1 mt-4 text-sm font-semibold first:mt-0">{children}</h4>
  ),
  p: ({children}) => (
    <p className="mb-3 leading-relaxed last:mb-0">{children}</p>
  ),
  ul: ({children}) => (
    <ul className="mb-3 ml-5 list-disc space-y-1 last:mb-0">{children}</ul>
  ),
  ol: ({children}) => (
    <ol className="mb-3 ml-5 list-decimal space-y-1 last:mb-0">{children}</ol>
  ),
  li: ({children}) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({children}) => (
    <blockquote className="mb-3 border-l-2 border-neutral-300 pl-3 italic text-neutral-600 last:mb-0">
      {children}
    </blockquote>
  ),
  a: ({href, children}) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 underline"
    >
      {children}
    </a>
  ),
  code({className, children}) {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <code className="font-mono text-xs">{children}</code>
    ) : (
      <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-xs">
        {children}
      </code>
    );
  },
  pre: ({children}) => (
    <pre className="mb-3 overflow-x-auto rounded bg-neutral-100 p-3 last:mb-0">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-4 border-neutral-200" />,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
  table: ({children}) => (
    <table className="mb-3 w-full border-collapse text-sm">{children}</table>
  ),
  th: ({children}) => (
    <th className="border border-neutral-200 bg-neutral-50 px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({children}) => (
    <td className="border border-neutral-200 px-2 py-1 align-top">
      {children}
    </td>
  ),
};

/**
 * Compact variant for small, dense surfaces (Otto Views' narrative block) —
 * same element vocabulary as ``markdownComponents``, tightened spacing and
 * text sized to inherit a ``text-xs``/``text-sm`` ambient size instead of the
 * prose-scale headings above, which would overpower a card this small.
 */
export const compactMarkdownComponents: Components = {
  h1: ({children}) => (
    <h4 className="mb-1 mt-3 text-xs font-semibold text-neutral-900 first:mt-0">
      {children}
    </h4>
  ),
  h2: ({children}) => (
    <h4 className="mb-1 mt-3 text-xs font-semibold text-neutral-900 first:mt-0">
      {children}
    </h4>
  ),
  h3: ({children}) => (
    <h4 className="mb-1 mt-2 text-xs font-semibold text-neutral-900 first:mt-0">
      {children}
    </h4>
  ),
  h4: ({children}) => (
    <h4 className="mb-1 mt-2 text-xs font-semibold text-neutral-900 first:mt-0">
      {children}
    </h4>
  ),
  p: ({children}) => (
    <p className="mb-2 leading-relaxed last:mb-0">{children}</p>
  ),
  ul: ({children}) => (
    <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>
  ),
  ol: ({children}) => (
    <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>
  ),
  li: ({children}) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({children}) => (
    <blockquote className="mb-2 border-l-2 border-neutral-300 pl-2.5 italic text-neutral-600 last:mb-0">
      {children}
    </blockquote>
  ),
  a: ({href, children}) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 underline"
    >
      {children}
    </a>
  ),
  code({className, children}) {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <code className="font-mono text-[11px]">{children}</code>
    ) : (
      <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-[11px]">
        {children}
      </code>
    );
  },
  pre: ({children}) => (
    <pre className="mb-2 overflow-x-auto rounded bg-neutral-100 p-2 last:mb-0">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-3 border-neutral-200" />,
  strong: ({children}) => (
    <strong className="font-semibold text-neutral-900">{children}</strong>
  ),
  table: ({children}) => (
    <table className="mb-2 w-full border-collapse text-[11px]">
      {children}
    </table>
  ),
  th: ({children}) => (
    <th className="border border-neutral-200 bg-neutral-50 px-1.5 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({children}) => (
    <td className="border border-neutral-200 px-1.5 py-1 align-top">
      {children}
    </td>
  ),
};
